'use client';

import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { type ChangeEvent, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { type PixelCrop } from 'react-image-crop';
import { Button } from '@module/mdrt-org/shared/components/button/button';
import { ProfilePhotoCropper } from '@shared/profile-dashboard-sections/profile-dashboard-hero-section/components/profile-photo-container/profile-photo-cropper';
import { ButtonVariant } from '@shared/ui-components/button/buttonVariant';
import { ChipTheme } from '@shared/ui-components/chip/chipTheme';
import { FileInput } from '@shared/ui-components/file-input/file-input';
import { ALLOWED_EXTENSIONS_INPUT_ACCEPT } from '@shared/utils/constants/fileUpload';
import { getCroppedFile } from '@shared/utils/helpers/get-cropped-file';
import photoPlaceholder from './photo-placeholder.png';
import styles from './photo-section.module.scss';
type PhotoSectionProps = {
  cmsContent: {
    addPhotoLabel: string;
    cancelLabel: string;
    changePhotoLabel: string;
    photoCropAriaLiveText: string;
    removePhotoLabel: string;
    saveChangesLabel: string;
  };
  profilePictureSource: Nullable<string>;
  savedProfilePictureSource: Nullable<string>;
  setSavedProfilePictureSource: (source: Nullable<string>) => void;
};
export const PhotoSection = t0 => {
  const $ = _c(41);
  const {
    cmsContent,
    profilePictureSource,
    savedProfilePictureSource,
    setSavedProfilePictureSource
  } = t0;
  const imgRef = useRef(null);
  const [croppedFile, setCroppedFile] = useState(null);
  const [cropConfirmed, setCropConfirmed] = useState(true);
  const [previewPictureSource, setPreviewPictureSource] = useState(profilePictureSource);
  const {
    resetField
  } = useFormContext();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = async crop => {
      if (!imgRef.current) {
        return;
      }
      const file = await getCroppedFile(imgRef.current, crop, undefined, 0.5);
      setCroppedFile(file);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleCropChange = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = event => {
      if (event && event.target?.files?.length) {
        const imageURL = URL.createObjectURL(event.target.files[0]);
        setCropConfirmed(false);
        setPreviewPictureSource(imageURL);
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const updatePreviewPictureSource = t2;
  let t3;
  if ($[2] !== resetField || $[3] !== setSavedProfilePictureSource) {
    t3 = () => {
      setCropConfirmed(true);
      setPreviewPictureSource(null);
      setSavedProfilePictureSource(null);
      resetField("profile-photo");
    };
    $[2] = resetField;
    $[3] = setSavedProfilePictureSource;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleRemovePhoto = t3;
  let t4;
  if ($[5] !== croppedFile || $[6] !== setSavedProfilePictureSource) {
    t4 = () => {
      if (croppedFile) {
        const imageURL_0 = URL.createObjectURL(croppedFile);
        setCropConfirmed(true);
        setSavedProfilePictureSource(imageURL_0);
        setPreviewPictureSource(imageURL_0);
      }
    };
    $[5] = croppedFile;
    $[6] = setSavedProfilePictureSource;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const onConfirmCrop = t4;
  let t5;
  if ($[8] !== resetField || $[9] !== savedProfilePictureSource) {
    t5 = () => {
      setCropConfirmed(true);
      setPreviewPictureSource(savedProfilePictureSource);
      resetField("profile-photo");
    };
    $[8] = resetField;
    $[9] = savedProfilePictureSource;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const onCancelCrop = t5;
  let t6;
  if ($[11] !== cropConfirmed) {
    t6 = classNames(styles["photo-section__photo-cropper"], {
      [styles["photo-section__photo-cropper--hidden"]]: cropConfirmed
    });
    $[11] = cropConfirmed;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = previewPictureSource || photoPlaceholder?.src;
  let t8;
  if ($[13] !== cmsContent.photoCropAriaLiveText || $[14] !== t6 || $[15] !== t7) {
    t8 = <div className={styles["photo-section__photo-container"]} data-testid="profile-photo-container"><ProfilePhotoCropper circular={false} className={t6} cropAriaLiveText={cmsContent.photoCropAriaLiveText} imgRef={imgRef} onCropChange={handleCropChange} src={t7} /></div>;
    $[13] = cmsContent.photoCropAriaLiveText;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = savedProfilePictureSource || previewPictureSource ? cmsContent.changePhotoLabel : cmsContent.addPhotoLabel;
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {};
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t9) {
    t11 = <FileInput accept={ALLOWED_EXTENSIONS_INPUT_ACCEPT} ariaOwns="profile-photo-cropper" asChip={true} dataTestId="profile-photo" hasError={false} label={t9} name="profile-photo" onChange={updatePreviewPictureSource} rules={t10} theme={ChipTheme.Light} variant={ButtonVariant.Secondary} />;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== cmsContent.removePhotoLabel || $[21] !== handleRemovePhoto || $[22] !== previewPictureSource || $[23] !== savedProfilePictureSource) {
    t12 = savedProfilePictureSource && savedProfilePictureSource === previewPictureSource && <Button isSmall={true} onClick={handleRemovePhoto}>{cmsContent.removePhotoLabel}</Button>;
    $[20] = cmsContent.removePhotoLabel;
    $[21] = handleRemovePhoto;
    $[22] = previewPictureSource;
    $[23] = savedProfilePictureSource;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t11 || $[26] !== t12) {
    t13 = <div className={styles["photo-section__primary-actions-container"]}>{t11}{t12}</div>;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== cmsContent.cancelLabel || $[29] !== cmsContent.saveChangesLabel || $[30] !== onCancelCrop || $[31] !== onConfirmCrop || $[32] !== previewPictureSource || $[33] !== savedProfilePictureSource) {
    t14 = previewPictureSource !== savedProfilePictureSource && <div className={styles["photo-section__toggled-actions-container"]}><Button isSmall={true} onClick={onConfirmCrop}>{cmsContent.saveChangesLabel}</Button><Button isSmall={true} onClick={onCancelCrop} variant={ButtonVariant.Plain}>{cmsContent.cancelLabel}</Button></div>;
    $[28] = cmsContent.cancelLabel;
    $[29] = cmsContent.saveChangesLabel;
    $[30] = onCancelCrop;
    $[31] = onConfirmCrop;
    $[32] = previewPictureSource;
    $[33] = savedProfilePictureSource;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t13 || $[36] !== t14) {
    t15 = <div className={styles["photo-section__actions-container"]}>{t13}{t14}</div>;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t15 || $[39] !== t8) {
    t16 = <section className={styles["photo-section"]} data-testid="photo-section">{t8}{t15}</section>;
    $[38] = t15;
    $[39] = t8;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  return t16;
};